import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

/**
 * The Activity Log — who did what, to which resource, with what outcome.
 * See `docs/UserActionAuditing.md`.
 *
 * Note the deliberate split between the list and detail shapes: the list omits
 * `details`/`beforeState`/`afterState` because a page of diffs would ship kilobytes
 * to render a table that shows none of them. The drawer fetches the full row.
 */

export interface ActivityEventSummary {
  id: string;
  occurredAt: string;
  action: string;
  category: string;
  outcome: 'success' | 'denied' | 'rejected' | 'failed';
  httpStatus: number;
  actorKind: string;
  actorUserId: string | null;
  /** Snapshotted at write time, so it still reads correctly for a deleted account. */
  actorLabel: string;
  viaAdmin: boolean;
  credentialKind: string | null;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  organizationId: string | null;
  sourceIp: string | null;
  /** Correlation id across services. Caller-influenced — `id` is the row's identity. */
  traceId: string;
}

export interface ActivityEventDetail extends ActivityEventSummary {
  contextId: string | null;
  actorServicePrincipalId: string | null;
  details: Record<string, unknown> | null;
  beforeState: unknown;
  afterState: unknown;
  reason: string | null;
  userAgent: string | null;
  spanId: string;
  parentSpanId: string | null;
  /** False for anything written before hash chaining was enabled. */
  chained: boolean;
}

export interface ActivityLogFilters {
  from?: string;
  to?: string;
  actorUserId?: string;
  /** Exact (`role:create`) or a resource prefix (`role:`). */
  action?: string;
  category?: string;
  outcome?: string;
  targetType?: string;
  targetId?: string;
  viaAdmin?: boolean;
}

interface ActivityEventPage {
  events: ActivityEventSummary[];
  nextCursor: string | null;
}

const PAGE_SIZE = 50;

function toParams(filters: ActivityLogFilters, cursor?: string): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  params.set('limit', String(PAGE_SIZE));
  if (cursor) params.set('cursor', cursor);
  return params;
}

/**
 * Keyset paging, not offset: the table only grows at the head, so an offset page
 * would shift under a reader as new events arrive.
 */
export function useActivityLog(filters: ActivityLogFilters) {
  return useInfiniteQuery({
    queryKey: ['activityLog', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await axios.get<ActivityEventPage>(
        `${config.backendApi}/api/activity-log?${toParams(filters, pageParam).toString()}`,
        { withCredentials: true },
      );
      return response.data;
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useActivityEvent(id: string | null) {
  return useQuery<ActivityEventDetail>({
    queryKey: ['activityLog', 'detail', id],
    enabled: id !== null,
    queryFn: async () => {
      // `enabled` above already guarantees this, but the guard is what tells the
      // compiler so — and a thrown error beats a request to `/activity-log/null`.
      if (id === null) throw new Error('no activity event selected');
      const response = await axios.get<ActivityEventDetail>(
        `${config.backendApi}/api/activity-log/${id}`,
        { withCredentials: true },
      );
      return response.data;
    },
  });
}
